import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Search,
  Building2,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Home,
  FileText,
  CreditCard,
  Users,
  Globe,
  Shield,
  Upload,
  ChevronRight,
  Clock,
  TrendingUp,
  Banknote,
  UserCheck,
  MapPin,
  FileSearch,
  ExternalLink,
  RefreshCw,
  Eye
} from "lucide-react";
import {
  runEligibilityCheck,
  getCreditsafeReport,
  searchCompaniesHouse,
  processBankStatementOCR,
  analyzeBankStatementData,
  checkDigitalFootprint,
  checkDirectorNetwork,
  runOnfidoCheck,
  runLandRegistrySearch,
  getUnderwritingStatus,
  getMondayItemFiles,
  processMondayFile
} from "@/lib/api";
import BankStatementViewer from "./BankStatementViewer";

// Stage configuration
const STAGES = [
  { id: "eligibility", label: "Eligibility", icon: Shield },
  { id: "credit", label: "Credit Assessment", icon: CreditCard },
  { id: "preoffer", label: "Pre-Offer", icon: UserCheck },
  { id: "final", label: "Final Checks", icon: FileSearch }
];

export default function UnderwritingPanel({ item, itemColumns, onUpdate }) {
  const [activeStage, setActiveStage] = useState("eligibility");
  const [loading, setLoading] = useState(false);
  const [underwritingData, setUnderwritingData] = useState(null);
  
  // Eligibility state
  const [companySearch, setCompanySearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isHomeowner, setIsHomeowner] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Bank statement state
  const [bankFile, setBankFile] = useState(null);
  const [bankData, setBankData] = useState(null);
  const [uploadingBank, setUploadingBank] = useState(false);
  const [showStatementViewer, setShowStatementViewer] = useState(false);
  const [mondayFiles, setMondayFiles] = useState([]);
  const [loadingMondayFiles, setLoadingMondayFiles] = useState(false);
  const [showMondayFilePicker, setShowMondayFilePicker] = useState(false);
  const [selectedMondayFiles, setSelectedMondayFiles] = useState([]);
  const [mondayFileSearch, setMondayFileSearch] = useState("");
  
  // Credit assessment state
  const [creditsafeReport, setCreditsafeReport] = useState(null);
  const [digitalFootprint, setDigitalFootprint] = useState(null);
  const [directorNetwork, setDirectorNetwork] = useState(null);
  
  // Pre-offer state
  const [directorCreditCheck, setDirectorCreditCheck] = useState(null);
  
  // Final checks state
  const [onfidoResult, setOnfidoResult] = useState(null);
  const [landRegistryResult, setLandRegistryResult] = useState(null);

  // Get application ID safely
  const getAppId = () => item?.id || item?.monday_item_id || null;

  // Load existing data when item changes
  useEffect(() => {
    const appId = getAppId();
    if (appId) {
      loadExistingData(appId);
    }
  }, [item?.id, item?.monday_item_id]);

  // Extract company name from item
  useEffect(() => {
    if (item?.name) {
      const companyName = item.name.split(' - ').slice(1).join(' - ') || item.name;
      setCompanySearch(companyName);
    }
  }, [item?.name]);

  const loadExistingData = async (appId) => {
    if (!appId) return;
    try {
      const data = await getUnderwritingStatus(appId);
      setUnderwritingData(data);
      
      if (data.eligibility_check) setEligibilityResult(data.eligibility_check);
      if (data.companies_house_data) setSelectedCompany(data.companies_house_data);
      if (data.creditsafe_report) setCreditsafeReport(data.creditsafe_report);
      if (data.bank_statement_ocr) setBankData(data.bank_statement_ocr);
    } catch (error) {
      // No existing data - that's fine
      console.log("No existing underwriting data");
    }
  };

  // Search Companies House
  const handleCompanySearch = async () => {
    if (!companySearch.trim()) return;
    
    setSearching(true);
    try {
      const result = await searchCompaniesHouse(companySearch, 5);
      setSearchResults(result.items || []);
    } catch (error) {
      toast.error("Failed to search Companies House");
    } finally {
      setSearching(false);
    }
  };

  // Run eligibility check
  const handleEligibilityCheck = async () => {
    if (!selectedCompany && !companySearch) {
      toast.error("Please search and select a company first");
      return;
    }
    
    const appId = getAppId();
    
    setLoading(true);
    try {
      const result = await runEligibilityCheck({
        application_id: appId,
        company_name: selectedCompany?.company_name || companySearch,
        company_number: selectedCompany?.company_number,
        is_homeowner: isHomeowner,
        monthly_sales: bankData?.average_monthly_credits || null
      });
      
      setEligibilityResult(result);
      if (result.company_data) {
        setSelectedCompany(result.company_data);
      }
      
      if (result.eligible) {
        toast.success("Eligibility check passed!");
      } else {
        toast.warning("Eligibility check failed - review flags");
      }
    } catch (error) {
      toast.error("Failed to run eligibility check");
    } finally {
      setLoading(false);
    }
  };

  // Upload bank statement
  const handleBankUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const appId = getAppId();
    
    setBankFile(file);
    setUploadingBank(true);
    
    try {
      const result = await processBankStatementOCR(file, appId);
      setBankData(result);
      toast.success("Bank statement processed successfully");
    } catch (error) {
      toast.error("Failed to process bank statement");
    } finally {
      setUploadingBank(false);
    }
  };

  // Load files from Monday.com
  const loadMondayFiles = async () => {
    const mondayItemId = item?.monday_item_id || item?.id;
    if (!mondayItemId) {
      toast.error("No Monday.com item ID available");
      return;
    }
    
    setLoadingMondayFiles(true);
    try {
      const result = await getMondayItemFiles(mondayItemId);
      setMondayFiles(result.files || []);
      setShowMondayFilePicker(true);
      
      if (result.files?.length === 0) {
        toast.info("No documents found on Monday.com item");
      } else {
        toast.success(`Found ${result.files.length} files on Monday.com`);
      }
    } catch (error) {
      toast.error("Failed to load files from Monday.com");
      console.error("Monday files error:", error);
    } finally {
      setLoadingMondayFiles(false);
    }
  };

  // Toggle file selection for multi-select
  const toggleMondayFileSelection = (file) => {
    setSelectedMondayFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  // Process selected files from Monday.com
  const handleProcessSelectedFiles = async () => {
    const mondayItemId = item?.monday_item_id || item?.id;
    if (!mondayItemId || selectedMondayFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }
    
    setShowMondayFilePicker(false);
    setUploadingBank(true);
    
    try {
      // Process the first selected file (bank statements should be PDFs)
      const bankStatementFiles = selectedMondayFiles.filter(f => 
        f.extension?.toLowerCase() === 'pdf' && 
        (f.is_bank_statement || f.name?.toLowerCase().includes('bank') || f.name?.toLowerCase().includes('statement'))
      );
      
      const fileToProcess = bankStatementFiles[0] || selectedMondayFiles[0];
      
      if (!fileToProcess.url) {
        throw new Error("Selected file has no download URL");
      }
      
      const result = await processMondayFile(mondayItemId, fileToProcess.url, fileToProcess.name);
      setBankData(result);
      toast.success(`Processed: ${fileToProcess.name}`);
      setSelectedMondayFiles([]);
    } catch (error) {
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Processing timed out. The file may be too large. Try again or use a smaller file.");
      } else if (error.response?.status === 504) {
        toast.error("Gateway timeout. The server is still processing. Please refresh and check if data was saved.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to process Monday.com file");
      }
      console.error("Monday file processing error:", error);
    } finally {
      setUploadingBank(false);
    }
  };

  // Filter Monday files by search
  const filteredMondayFiles = mondayFiles.filter(f => 
    !mondayFileSearch || 
    f.name?.toLowerCase().includes(mondayFileSearch.toLowerCase()) ||
    f.extension?.toLowerCase().includes(mondayFileSearch.toLowerCase())
  );

  // Run Creditsafe report
  const handleCreditsafeReport = async () => {
    if (!selectedCompany?.company_name) {
      toast.error("Please complete eligibility check first");
      return;
    }
    
    const appId = getAppId();
    
    setLoading(true);
    try {
      const result = await getCreditsafeReport(
        selectedCompany.company_name,
        selectedCompany.company_number,
        appId
      );
      setCreditsafeReport(result);
      
      if (!result.error) {
        toast.success(`Creditsafe report fetched - Score: ${result.credit_score || 'N/A'}`);
      } else {
        toast.warning(result.error);
      }
    } catch (error) {
      toast.error("Failed to fetch Creditsafe report");
    } finally {
      setLoading(false);
    }
  };

  // Check digital footprint
  const handleDigitalFootprint = async () => {
    const companyName = selectedCompany?.company_name || companySearch;
    if (!companyName) return;
    
    setLoading(true);
    try {
      const result = await checkDigitalFootprint(companyName);
      setDigitalFootprint(result);
      toast.success("Digital footprint check complete");
    } catch (error) {
      toast.error("Failed to check digital footprint");
    } finally {
      setLoading(false);
    }
  };

  // Check director network
  const handleDirectorNetwork = async () => {
    const directorName = selectedCompany?.directors?.[0]?.name || 
                        itemColumns?.['Director Name'] || 
                        itemColumns?.['director_name'];
    
    if (!directorName) {
      toast.warning("No director name available");
      return;
    }
    
    setLoading(true);
    try {
      const result = await checkDirectorNetwork(directorName);
      setDirectorNetwork(result);
      toast.success("Director network check complete");
    } catch (error) {
      toast.error("Failed to check director network");
    } finally {
      setLoading(false);
    }
  };

  // Run Onfido check (mock)
  const handleOnfidoCheck = async () => {
    const directorName = selectedCompany?.directors?.[0]?.name || 
                        itemColumns?.['Director Name'];
    
    if (!directorName) {
      toast.warning("No director name available");
      return;
    }
    
    const appId = getAppId();
    
    setLoading(true);
    try {
      const result = await runOnfidoCheck(directorName, appId);
      setOnfidoResult(result);
      toast.info("Onfido check complete (MOCK)");
    } catch (error) {
      toast.error("Failed to run Onfido check");
    } finally {
      setLoading(false);
    }
  };

  // Run Land Registry search (mock)
  const handleLandRegistry = async () => {
    const address = itemColumns?.['Address'] || itemColumns?.['Property Address'] || "";
    const postcode = itemColumns?.['Postcode'] || "";
    
    if (!address) {
      toast.warning("No address available for Land Registry search");
      return;
    }
    
    const appId = getAppId();
    
    setLoading(true);
    try {
      const result = await runLandRegistrySearch(address, postcode, appId);
      setLandRegistryResult(result);
      toast.info("Land Registry search complete (MOCK)");
    } catch (error) {
      toast.error("Failed to search Land Registry");
    } finally {
      setLoading(false);
    }
  };

  // Calculate stage completion
  const getStageCompletion = (stageId) => {
    switch (stageId) {
      case "eligibility":
        let count = 0;
        if (selectedCompany) count++;
        if (eligibilityResult) count++;
        if (bankData) count++;
        return (count / 3) * 100;
      case "credit":
        let creditCount = 0;
        if (creditsafeReport) creditCount++;
        if (digitalFootprint) creditCount++;
        if (directorNetwork) creditCount++;
        return (creditCount / 3) * 100;
      case "preoffer":
        return directorCreditCheck ? 100 : 0;
      case "final":
        let finalCount = 0;
        if (onfidoResult) finalCount++;
        if (landRegistryResult) finalCount++;
        return (finalCount / 2) * 100;
      default:
        return 0;
    }
  };

  const getStatusBadge = (passed) => {
    if (passed === true) {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Passed</Badge>;
    } else if (passed === false) {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
    }
    return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  // Guard: Show message if no item selected
  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <Shield className="w-12 h-12 text-slate-300 mb-4" />
        <h3 className="font-medium text-slate-600">No Application Selected</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Select an application from the list to start underwriting
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stage Progress Bar */}
      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
        {STAGES.map((stage, idx) => (
          <div key={stage.id} className="flex items-center flex-1">
            <button
              onClick={() => setActiveStage(stage.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all w-full ${
                activeStage === stage.id 
                  ? 'bg-emerald-600 text-white' 
                  : getStageCompletion(stage.id) === 100 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-white hover:bg-slate-100'
              }`}
            >
              <stage.icon className="w-4 h-4" />
              <span className="text-xs font-medium hidden lg:inline">{stage.label}</span>
              {getStageCompletion(stage.id) === 100 && activeStage !== stage.id && (
                <CheckCircle2 className="w-3 h-3 ml-auto" />
              )}
            </button>
            {idx < STAGES.length - 1 && (
              <ChevronRight className="w-4 h-4 mx-1 text-slate-300 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>

      <ScrollArea className="h-[calc(100vh-380px)]">
        {/* Stage 1: Eligibility */}
        {activeStage === "eligibility" && (
          <div className="space-y-4 pr-2">
            {/* Companies House Search */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Companies House Lookup
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search company name..."
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCompanySearch()}
                    data-testid="company-search-input"
                  />
                  <Button onClick={handleCompanySearch} disabled={searching} variant="secondary">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && !selectedCompany && (
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {searchResults.map((company, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSelectedCompany({
                            company_name: company.title,
                            company_number: company.company_number,
                            company_status: company.company_status,
                            incorporation_date: company.date_of_creation,
                            registered_office: company.address_snippet
                          });
                          setSearchResults([]);
                        }}
                        className="w-full p-3 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="font-medium text-sm">{company.title}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                          <span>{company.company_number}</span>
                          <span>•</span>
                          <Badge variant={company.company_status === 'active' ? 'default' : 'secondary'} className="text-[10px]">
                            {company.company_status}
                          </Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Selected Company Card */}
                {selectedCompany && (
                  <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-sm flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          {selectedCompany.company_name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                          <div>Company No: {selectedCompany.company_number}</div>
                          <div>Status: {selectedCompany.company_status}</div>
                          {selectedCompany.incorporation_date && (
                            <div>Incorporated: {selectedCompany.incorporation_date}</div>
                          )}
                        </div>
                        {selectedCompany.directors?.length > 0 && (
                          <div className="mt-2 text-xs">
                            <span className="font-medium">Directors: </span>
                            {selectedCompany.directors.map(d => d.name).join(', ')}
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedCompany(null)}
                      >
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Homeowner Check */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Home className="w-5 h-5 text-slate-600" />
                    <div>
                      <Label className="text-sm font-medium">Director is Homeowner?</Label>
                      <p className="text-xs text-muted-foreground">
                        Homeowner: 6 months trading | Non-homeowner: 12 months
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="homeowner"
                      checked={isHomeowner}
                      onCheckedChange={setIsHomeowner}
                      data-testid="homeowner-checkbox"
                    />
                    <Label htmlFor="homeowner" className="text-sm">
                      {isHomeowner ? "Yes" : "No"}
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Statement Upload */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Bank Statement Analysis
                  {bankData && (
                    <Badge className={`ml-2 ${
                      bankData.processing_status === 'SUCCESS' ? 'bg-green-100 text-green-700' :
                      bankData.processing_status === 'PARTIAL' ? 'bg-amber-100 text-amber-700' :
                      bankData.processing_status === 'FAILED' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {bankData.processing_status || 'Uploaded'}
                    </Badge>
                  )}
                  {bankData?.source === 'monday.com' && (
                    <Badge className="ml-1 bg-purple-100 text-purple-700">Monday.com</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  Minimum £5,000/month sales required • AI-powered analysis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Upload Options */}
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={handleBankUpload}
                    disabled={uploadingBank}
                    className="text-xs flex-1"
                    data-testid="bank-upload-input"
                  />
                  <span className="text-xs text-muted-foreground">or</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadMondayFiles}
                    disabled={uploadingBank || loadingMondayFiles}
                    className="text-xs whitespace-nowrap"
                    data-testid="monday-files-btn"
                  >
                    {loadingMondayFiles ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <FileText className="w-3 h-3 mr-1" />
                    )}
                    Monday.com Files
                  </Button>
                </div>
                
                {/* Monday.com File Picker with Search and Multi-Select */}
                {showMondayFilePicker && mondayFiles.length > 0 && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-700">
                        Select files from Monday.com ({selectedMondayFiles.length} selected)
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setShowMondayFilePicker(false);
                          setSelectedMondayFiles([]);
                          setMondayFileSearch("");
                        }}
                        className="h-6 w-6 p-0"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Search input */}
                    <div className="relative mb-2">
                      <Search className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search files..."
                        value={mondayFileSearch}
                        onChange={(e) => setMondayFileSearch(e.target.value)}
                        className="pl-7 h-8 text-xs"
                      />
                    </div>
                    
                    {/* File list with checkboxes */}
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {filteredMondayFiles.map((file, idx) => {
                        const isSelected = selectedMondayFiles.some(f => f.id === file.id);
                        return (
                          <div
                            key={file.id || idx}
                            onClick={() => toggleMondayFileSelection(file)}
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                              isSelected ? 'border-purple-500 bg-purple-100' : 
                              file.is_bank_statement ? 'border-purple-300 bg-purple-50 hover:bg-purple-100' : 
                              'border-slate-200 bg-white hover:bg-slate-50'
                            }`}
                          >
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleMondayFileSelection(file)}
                              className="pointer-events-none"
                            />
                            <FileText className={`w-4 h-4 flex-shrink-0 ${
                              file.is_bank_statement ? 'text-purple-600' : 'text-slate-500'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium truncate">{file.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {file.extension?.toUpperCase()} • {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                              </div>
                            </div>
                            {file.is_bank_statement && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs flex-shrink-0">Statement</Badge>
                            )}
                          </div>
                        );
                      })}
                      {filteredMondayFiles.length === 0 && (
                        <div className="text-xs text-center text-muted-foreground py-4">
                          No files match your search
                        </div>
                      )}
                    </div>
                    
                    {/* Process button */}
                    <Button
                      onClick={handleProcessSelectedFiles}
                      disabled={selectedMondayFiles.length === 0 || uploadingBank}
                      className="w-full mt-2 bg-purple-600 hover:bg-purple-700 text-xs h-8"
                      size="sm"
                    >
                      {uploadingBank ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          Processing...
                        </>
                      ) : (
                        <>Process Selected ({selectedMondayFiles.length})</>
                      )}
                    </Button>
                  </div>
                )}
                
                {uploadingBank && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </div>
                )}
                
                {/* Processing Progress */}
                {uploadingBank && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 text-sm text-blue-700 mb-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="font-medium">Processing Bank Statement</span>
                    </div>
                    <div className="text-xs text-blue-600 space-y-1">
                      <div>1. Uploading file to secure storage...</div>
                      <div>2. Running OCR extraction...</div>
                      <div>3. AI analysis of financial data...</div>
                    </div>
                    <div className="text-xs text-amber-600 mt-2">
                      This can take 30-60 seconds for large files. Please wait...
                    </div>
                  </div>
                )}
                
                {/* Error State */}
                {bankData?.error && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 text-sm text-red-700 mb-1">
                      <XCircle className="w-4 h-4" />
                      <span className="font-medium">Processing Failed</span>
                    </div>
                    <div className="text-xs text-red-600">{bankData.error}</div>
                    {bankData.error_step && (
                      <div className="text-xs text-muted-foreground mt-1">Failed at: {bankData.error_step}</div>
                    )}
                  </div>
                )}
                
                {/* Success Results */}
                {bankData && !bankData.error && (
                  <div className="space-y-3">
                    {/* Main Metrics */}
                    <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="text-center">
                        <div className="text-lg font-bold text-emerald-600">
                          £{(bankData.average_monthly_credits || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">Avg Monthly Credits</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-lg font-bold ${bankData.meets_min_sales ? 'text-green-600' : 'text-red-600'}`}>
                          {bankData.meets_min_sales ? <CheckCircle2 className="w-5 h-5 mx-auto" /> : <XCircle className="w-5 h-5 mx-auto" />}
                        </div>
                        <div className="text-xs text-muted-foreground">Meets £5k Min</div>
                      </div>
                    </div>
                    
                    {/* Financial Summary */}
                    {bankData.financial_summary && (
                      <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                        <div className="text-xs font-medium text-slate-700 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Financial Summary
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>Total Credits: <span className="font-medium text-green-600">£{(bankData.financial_summary.total_credits || 0).toLocaleString()}</span></div>
                          <div>Total Debits: <span className="font-medium text-red-600">£{(bankData.financial_summary.total_debits || 0).toLocaleString()}</span></div>
                          {bankData.financial_summary.opening_balance && (
                            <div>Opening: <span className="font-medium">£{bankData.financial_summary.opening_balance.toLocaleString()}</span></div>
                          )}
                          {bankData.financial_summary.closing_balance && (
                            <div>Closing: <span className="font-medium">£{bankData.financial_summary.closing_balance.toLocaleString()}</span></div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Risk Factors */}
                    {bankData.risk_factors && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                        <div className="text-xs font-medium text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Risk Indicators
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            {bankData.risk_factors.bounced_payments > 0 ? (
                              <XCircle className="w-3 h-3 text-red-500" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            )}
                            <span>Bounced: {bankData.risk_factors.bounced_payments || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {bankData.risk_factors.overdraft_usage ? (
                              <XCircle className="w-3 h-3 text-amber-500" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            )}
                            <span>Overdraft: {bankData.risk_factors.overdraft_usage ? 'Yes' : 'No'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {bankData.risk_factors.gambling_transactions ? (
                              <XCircle className="w-3 h-3 text-red-500" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            )}
                            <span>Gambling: {bankData.risk_factors.gambling_transactions ? 'Yes' : 'No'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {bankData.risk_factors.high_cash_withdrawals ? (
                              <XCircle className="w-3 h-3 text-amber-500" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            )}
                            <span>High Cash: {bankData.risk_factors.high_cash_withdrawals ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                        {bankData.risk_factors.unusual_patterns?.length > 0 && (
                          <div className="text-xs text-amber-600 mt-1">
                            <span className="font-medium">Unusual:</span> {bankData.risk_factors.unusual_patterns.join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Affordability Recommendation */}
                    {bankData.affordability_indicators && (
                      <div className={`p-3 rounded-lg border ${
                        bankData.affordability_indicators.recommendation === 'APPROVED' ? 'bg-green-50 border-green-200' :
                        bankData.affordability_indicators.recommendation === 'REVIEW' ? 'bg-amber-50 border-amber-200' :
                        'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium">AI Recommendation</div>
                          <Badge className={`${
                            bankData.affordability_indicators.recommendation === 'APPROVED' ? 'bg-green-100 text-green-700' :
                            bankData.affordability_indicators.recommendation === 'REVIEW' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {bankData.affordability_indicators.recommendation || 'PENDING'}
                          </Badge>
                        </div>
                        {bankData.affordability_indicators.disposable_income && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Disposable Income: £{bankData.affordability_indicators.disposable_income.toLocaleString()}/month
                          </div>
                        )}
                        {bankData.affordability_indicators.stability_score && (
                          <div className="text-xs text-muted-foreground">
                            Stability Score: {bankData.affordability_indicators.stability_score}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Open Statements Button */}
                    <Button 
                      variant="outline" 
                      className="w-full mt-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={() => setShowStatementViewer(true)}
                      data-testid="open-statements-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Open Statements
                    </Button>
                    
                    {/* AI Notes */}
                    {bankData.notes && (
                      <div className="p-2 bg-slate-100 rounded text-xs text-muted-foreground">
                        <span className="font-medium">Notes:</span> {bankData.notes}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Eligibility Check Result */}
            {eligibilityResult && (
              <Card className={eligibilityResult.eligible ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {eligibilityResult.eligible ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    Eligibility: {eligibilityResult.eligible ? "PASSED" : "FAILED"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {eligibilityResult.checks?.map((check, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs p-2 bg-white rounded">
                      <span>{check.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{check.value}</span>
                        {check.passed ? (
                          <CheckCircle2 className="w-3 h-3 text-green-600" />
                        ) : (
                          <XCircle className="w-3 h-3 text-red-600" />
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {eligibilityResult.flags?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {eligibilityResult.flags.map((flag, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-red-700 p-2 bg-red-100 rounded">
                          <AlertTriangle className="w-3 h-3" />
                          {flag.message}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Run Eligibility Button */}
            <Button 
              onClick={handleEligibilityCheck} 
              className="w-full"
              disabled={loading || !selectedCompany}
              data-testid="run-eligibility-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running Checks...</>
              ) : (
                <><Shield className="w-4 h-4 mr-2" />Run Eligibility Check</>
              )}
            </Button>
          </div>
        )}

        {/* Stage 2: Credit Assessment */}
        {activeStage === "credit" && (
          <div className="space-y-4 pr-2">
            {/* Creditsafe Report */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Creditsafe Company Report
                  {creditsafeReport && !creditsafeReport.error && (
                    <Badge className="ml-auto bg-green-100 text-green-700">Score: {creditsafeReport.credit_score || 'N/A'}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!creditsafeReport ? (
                  <Button onClick={handleCreditsafeReport} disabled={loading} className="w-full" variant="outline">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Fetch Credit Report
                  </Button>
                ) : creditsafeReport.error ? (
                  <div className="text-sm text-red-600 p-3 bg-red-50 rounded">{creditsafeReport.error}</div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-slate-50 rounded text-center">
                        <div className="text-2xl font-bold text-emerald-600">{creditsafeReport.credit_score || 'N/A'}</div>
                        <div className="text-xs text-muted-foreground">Credit Score</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded text-center">
                        <div className="text-lg font-bold">{creditsafeReport.ccj_count || 0}</div>
                        <div className="text-xs text-muted-foreground">CCJs</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded text-center">
                        <div className="text-lg font-bold">£{(creditsafeReport.credit_limit || 0).toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">Credit Limit</div>
                      </div>
                    </div>
                    {creditsafeReport.payment_data && (
                      <div className="text-xs p-2 bg-slate-50 rounded">
                        <span className="font-medium">DBT: </span>{creditsafeReport.payment_data.dbt_days || 'N/A'} days
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Digital Footprint */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Digital Footprint
                  {digitalFootprint && !digitalFootprint.error && (
                    <Badge className="ml-auto">{digitalFootprint.reputation_score}/5</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!digitalFootprint ? (
                  <Button onClick={handleDigitalFootprint} disabled={loading} className="w-full" variant="outline">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                    Check Digital Footprint
                  </Button>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 bg-green-50 rounded text-center">
                      <div className="font-bold text-green-600">{digitalFootprint.review_indicators?.positive_signals || 0}</div>
                      <div className="text-[10px] text-muted-foreground">Positive</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded text-center">
                      <div className="font-bold">{digitalFootprint.review_indicators?.neutral_signals || 0}</div>
                      <div className="text-[10px] text-muted-foreground">Neutral</div>
                    </div>
                    <div className="p-2 bg-red-50 rounded text-center">
                      <div className="font-bold text-red-600">{digitalFootprint.review_indicators?.negative_signals || 0}</div>
                      <div className="text-[10px] text-muted-foreground">Negative</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Director Network */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Director Network Check
                  {directorNetwork && !directorNetwork.error && (
                    directorNetwork.insolvency_flags?.length > 0 ? (
                      <Badge className="ml-auto bg-red-100 text-red-700">{directorNetwork.insolvency_flags.length} Issues</Badge>
                    ) : (
                      <Badge className="ml-auto bg-green-100 text-green-700">Clear</Badge>
                    )
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!directorNetwork ? (
                  <Button onClick={handleDirectorNetwork} disabled={loading} className="w-full" variant="outline">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                    Check Director Network
                  </Button>
                ) : directorNetwork.insolvency_flags?.length > 0 ? (
                  <div className="space-y-2">
                    {directorNetwork.insolvency_flags.map((flag, idx) => (
                      <div key={idx} className="p-2 bg-red-50 border border-red-200 rounded text-xs">
                        <div className="font-medium">{flag.company}</div>
                        <div className="text-muted-foreground">Status: {flag.status} | Role: {flag.role}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-700 p-3 bg-green-50 rounded">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm">No insolvencies found</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stage 3: Pre-Offer */}
        {activeStage === "preoffer" && (
          <div className="space-y-4 pr-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  Director Consumer Credit Check
                </CardTitle>
                <CardDescription className="text-xs">
                  Personal credit check on the director/guarantor
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" variant="outline" disabled>
                  <UserCheck className="w-4 h-4 mr-2" />
                  Run Consumer Credit Check
                  <Badge className="ml-2" variant="secondary">Coming Soon</Badge>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <h3 className="font-medium mb-1">JotForm Offer Embed</h3>
                <p className="text-sm text-muted-foreground">
                  Offer creation form will be embedded here
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stage 4: Final Checks */}
        {activeStage === "final" && (
          <div className="space-y-4 pr-2">
            {/* Onfido */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Digital ID Verification (Onfido)
                  {onfidoResult && <Badge className="ml-auto" variant="secondary">MOCK</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!onfidoResult ? (
                  <Button onClick={handleOnfidoCheck} disabled={loading} className="w-full" variant="outline">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                    Run ID Verification
                  </Button>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">MOCK Response</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Status: {onfidoResult.verification_status} | 
                      Checks: {onfidoResult.checks_completed?.join(', ')}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Land Registry */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Land Registry Search
                  {landRegistryResult && <Badge className="ml-auto" variant="secondary">MOCK</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!landRegistryResult ? (
                  <Button onClick={handleLandRegistry} disabled={loading} className="w-full" variant="outline">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                    Search Land Registry
                  </Button>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">MOCK Response</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Title: {landRegistryResult.title_number} | 
                      Tenure: {landRegistryResult.tenure}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </ScrollArea>
      
      {/* Bank Statement Viewer Modal */}
      <BankStatementViewer
        open={showStatementViewer}
        onClose={() => setShowStatementViewer(false)}
        bankData={bankData}
        applicationInfo={{
          company_name: selectedCompany?.company_name || companySearch,
          application_id: getAppId()
        }}
      />
    </div>
  );
}
